import { MicOff } from 'lucide-react'
import { getMeetingHoursForPlan } from '@highlight/common'
import type { BillingPlanV2Tier } from '@highlight/common/types'
import { BillingRefreshNotice } from '@renderer/components/pricing/billing-refresh-notice'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { getUpgradeTarget, v2TierFor } from '@renderer/chat/utils/billingUtils'
import { TopUpPaywall } from '@renderer/components/pricing/top-up-paywall'
import { PlanUpgradePaywall } from '@renderer/components/pricing/plan-paywall'
import { MEETING_HOURS_TOP_UP } from '@renderer/components/pricing/top-up-kinds'
import { usePaywallShown, usePlanUpgrade, useTopUp } from '@renderer/components/pricing/use-top-up-actions'
import { hoursOutCopy, resetDateLabel } from '@renderer/components/pricing/limits-copy'
import { LockNotice, PlanUpgradeButton } from '@renderer/components/pricing/locked-page'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { subscriptionPlansRoute, subscriptionTopUpRoute } from '@shared/settings/settings-deeplink'
import { openSubscriptionSettings } from '../../../components/sidepanel/plan-banners/sidepanel-plan-banners'
import { useSidepanelFloatingBarHeight } from '../../../atoms'
import { useOutOfMeetingHours } from '../../../components/meeting/use-out-of-meeting-hours'
import type { MeetingViewModel } from '../use-meeting-view-model'

/** The floating-card chrome, owned here because only this placement is a card. */
const DOCKED_CARD_CLASSES = 'rounded-2xl corner-smooth shadow-md'

/** Gap between the docked card and the footer chrome below it. */
const PAYWALL_BOTTOM_GAP_PX = 12

/**
 * Whether the out-of-hours paywall occupies the meeting view: a v2 plan's
 * meeting-hours allowance is spent, on a meeting that can still be recorded.
 * Shared with the footer's mode-notice slot, which hides while this shows:
 * its "Highlight will automatically transcribe your meeting" would be a false
 * promise next to a card saying the hours are gone.
 */
export function useHoursPaywallVisible(model: MeetingViewModel): boolean {
  const outOfHours = useOutOfMeetingHours()
  const paywall = usePaywallState()
  const pricingEdition = usePricingEdition()
  // Only a plan that grants no meeting time at all, which is free. A tier-less
  // plan is not enough: legacy pro and eat hold an allowance (unlimited, in
  // eat's case) that isOutOfMeetingHours exempts, and pitching them an upgrade
  // "to transcribe your meetings" on every upcoming meeting would be a lie.
  const needsPlan = !paywall.isLoading && getMeetingHoursForPlan(paywall.plan, pricingEdition) === 0
  // Not the latched isUpcomingWithoutAudioNote: a happening-now cell drills
  // into this view, and its Join CTA is exactly where the hours are needed.
  // Same predicate as the header's Join button, minus the link.
  const isMeetingOver = !!model.endedAt && model.endedAt.getTime() < Date.now()
  const canStillRecord = model.variant !== 'live' && !model.audioNoteId && !isMeetingOver
  return (outOfHours || needsPlan) && canStillRecord
}

/**
 * The out-of-hours paywall, docked below the body of the upcoming-meeting
 * view when a v2 plan's meeting-hours allowance is spent. In flow rather than
 * overlaid, so the body shrinks to the space above it and the Get Ready hero
 * centers in what remains instead of disappearing behind the card. Legacy
 * plans keep `meetingHours.total` null, so only the v2 tiers can trip this.
 * Only while the meeting can still be recorded: the meeting-start gate already
 * refuses over-limit recordings, so live and recorded views never reach this
 * state mid-view.
 */
export function MeetingViewHoursPaywall({ model }: { model: MeetingViewModel }) {
  const paywall = usePaywallState()
  const visible = useHoursPaywallVisible(model)
  const isRevamp = usePricingEdition() === 'v2-revamp'
  const tier = v2TierFor(paywall.plan)

  if (!visible) {
    return null
  }
  if (isRevamp) {
    return <HoursOutNotice tier={tier} />
  }
  return tier ? <DockedPaywall tier={tier} /> : <DockedPlanPromo />
}

/** The revamp's notice: the limit and one way on, which opens Usage & billing to buy or upgrade. */
function HoursOutNotice({ tier }: { tier: BillingPlanV2Tier | null }) {
  const { subscription } = useUnifiedBilling()
  const plan = tier ?? 'pro2'
  const copy = hoursOutCopy({
    plan: tier ?? 'free',
    upgradeTo: 'pro2',
    resets: resetDateLabel(subscription.currentPeriodEndAt),
  })
  const reason = tier ? 'top-up' : 'upgrade'
  usePaywallShown({ tier: plan, from: 'meeting view' }, MEETING_HOURS_TOP_UP.analytics.feature, 'limit-reached')

  const onAction = () => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'clicked',
      feature: 'hours-out',
      reason,
      plan,
      from: 'meeting view',
    })
    openSubscriptionSettings(tier ? subscriptionTopUpRoute('hours') : subscriptionPlansRoute())
  }

  return (
    <DockedSlot>
      <LockNotice
        className="static"
        icon={<MicOff className="size-5" />}
        plan={plan}
        title={copy.title}
        description={copy.body}
        actions={
          <PlanUpgradeButton plan={plan} onClick={onAction}>
            {copy.cta}
          </PlanUpgradeButton>
        }
      />
    </DockedSlot>
  )
}

/**
 * The plan promo in the same slot, for the plans that hold no tier. Free grants
 * zero meeting hours, so an upcoming meeting is exactly where the plan has to
 * be sold.
 */
function DockedPlanPromo() {
  const paywall = usePaywallState()
  const edition = usePricingEdition()
  const { billingProfile } = useUnifiedBilling()
  const target = getUpgradeTarget(paywall.plan, billingProfile?.previousPlan)
  const surface = { tier: target ?? 'pro2', from: 'meeting view' }
  usePaywallShown(surface, MEETING_HOURS_TOP_UP.analytics.feature, 'requires-plan')
  const { onUpgrade, hasPaymentMethod, isPending, errorMessage, needsBillingRefresh, clearBillingRefresh } =
    usePlanUpgrade(surface)

  // Null only for a paid v2 tier, which resolves a tier and takes the branch above.
  if (!target) return null
  return (
    <DockedSlot>
      {needsBillingRefresh ? (
        <BillingRefreshNotice onRefreshed={clearBillingRefresh} className={DOCKED_CARD_CLASSES} />
      ) : (
        <PlanUpgradePaywall
          plan={target}
          edition={edition}
          discount={billingProfile?.discount ?? null}
          onUpgrade={onUpgrade}
          hasPaymentMethod={hasPaymentMethod}
          isPending={isPending}
          errorMessage={errorMessage}
          className={DOCKED_CARD_CLASSES}
        />
      )}
    </DockedSlot>
  )
}

/**
 * The footer is in flow; only the floating toolbar overlays this tail, so the
 * margin just has to clear that.
 */
function DockedSlot({ children }: { children: React.ReactNode }) {
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  return (
    <div className="relative shrink-0 px-3" style={{ marginBottom: floatingBarHeight + PAYWALL_BOTTOM_GAP_PX }}>
      {children}
    </div>
  )
}

function DockedPaywall({ tier }: { tier: BillingPlanV2Tier }) {
  const surface = { tier, from: 'meeting view' }
  usePaywallShown(surface, MEETING_HOURS_TOP_UP.analytics.feature, 'limit-reached')
  // Success needs no handling beyond the refresh: the extra hours lower the
  // usage percentage and this card unmounts itself.
  const { onPurchase, onUpgrade, isPending, isUpgradePending, errorMessage, needsBillingRefresh, clearBillingRefresh } =
    useTopUp(MEETING_HOURS_TOP_UP, surface)

  return (
    <DockedSlot>
      {needsBillingRefresh ? (
        <BillingRefreshNotice onRefreshed={clearBillingRefresh} className={DOCKED_CARD_CLASSES} />
      ) : (
        <TopUpPaywall
          spec={MEETING_HOURS_TOP_UP}
          plan={tier}
          atLimit
          className={DOCKED_CARD_CLASSES}
          onPurchase={onPurchase}
          onUpgrade={onUpgrade}
          isPending={isPending}
          isUpgradePending={isUpgradePending}
          errorMessage={errorMessage}
        />
      )}
    </DockedSlot>
  )
}
